// Shared account settings, intentionally used by both BayHive and Autoflow.
// Persistence encodings and consumers: docs/bayhive-admin/shared-controls.md.
export const FEATURE_GROUPS = [
  { key: 'workflow', label: 'Application features', autosave: true },
  { key: 'communications', label: 'Communication & security', autosave: true },
  { key: 'marketing', label: 'Marketing & reminders', autosave: true },
  {
    key: 'vehicle',
    label: 'Vehicle & inspection integrations',
    description: 'Provider connections must be configured before enabling their features.',
    autosave: true,
  },
  {
    key: 'payments',
    label: 'Payment services',
    description:
      'Use the shop’s existing provider connections. Enabling a service does not open a merchant account.',
    autosave: false,
    confirmation:
      'Update the payment services available to this shop? Existing provider credentials will be kept.',
  },
  { key: 'media', label: 'Inspection photos & video', autosave: true },
  {
    key: 'ai',
    label: 'AI assistance',
    autosave: false,
    confirmation:
      'Update AI access and the selected plan for this shop? The selected plan determines the daily usage limit.',
  },
];

const toggle = (key, label, group, extra = {}) => ({
  key,
  label,
  group,
  type: 'boolean',
  ...extra,
});
export const FEATURE_CONTROLS = [
  toggle('inspect_addon', 'DVI', 'workflow', { setup: true }),
  toggle('qc_form_addon', 'Quality control', 'workflow', { setup: true }),
  toggle('workorder_addon', 'Repair order workflow', 'workflow', {
    setup: true,
    help: 'Includes repair order and bay schedule permissions.',
  }),
  toggle('repairorder_addon', 'Repair order reports & notifications', 'workflow'),
  toggle('idvi', 'iDVI invitations', 'workflow'),
  toggle('timeclock', 'Time Clock', 'workflow', { setup: true }),
  toggle('internalchat', 'Internal chat', 'communications', {
    setup: true,
    help: 'Controls shared chat and notification settings. BayHive chat remains available.',
  }),
  toggle('voice_agent', 'Voice Agent', 'communications', {
    help: 'Phone provisioning is managed in Voice Agents.',
  }),
  toggle('secure_passwords', 'Secure passwords', 'communications', { sensitive: true }),
  toggle('text_opt_signature', 'Require texting consent signature', 'communications', {
    sensitive: true,
  }),
  toggle('disable_text_after_visit', 'Disable texting after a visit', 'communications', {
    sensitive: true,
  }),
  {
    key: 'disable_text_after_visit_message',
    label: 'After-visit reply',
    group: 'communications',
    type: 'textarea',
    max: 1000,
    help: 'Reply used when after-visit texting is disabled.',
  },
  {
    key: 'phone_provider',
    label: 'Caller ID provider',
    group: 'communications',
    type: 'select',
    optionsKey: 'phoneProviders',
    allowedValues: ['ring_central', 'twilio', 'inbound', 'goto'],
    placeholder: 'None',
    sensitive: true,
  },
  toggle('marketing_addon', 'Marketing', 'marketing', { setup: true }),
  toggle('autorewards_addon', 'Rewards & referrals', 'marketing', { setup: true }),
  toggle('service_reminders', 'Service reminders', 'marketing', { setup: true }),
  toggle('automate_service_reminders', 'Automated service reminders', 'marketing', {
    help: 'Requires Service reminders to be enabled.',
  }),
  toggle('google_reserve', 'Reserve with Google', 'marketing', {
    help: 'Requires an active booking link and Google registration.',
    sensitive: true,
  }),
  toggle('license_decoder', 'License plate lookup', 'vehicle', {
    help: 'Controls shared plate-lookup tools. BayHive vehicle lookup remains available.',
  }),
  toggle('carfax', 'Carfax', 'vehicle'),
  toggle('virtual_vehicle', 'MotoVisuals', 'vehicle'),
  toggle('autonettv', 'Autonet TV', 'vehicle'),
  toggle('hunter', 'Hunter', 'vehicle'),
  toggle('groove_glove', 'Groove Glove', 'vehicle'),
  toggle('tire_connect', 'TireConnect', 'vehicle', {
    help: 'Shared TireConnect setting. Booking availability follows the provider connection.',
  }),
  toggle('360_payments', '360 Payments', 'payments'),
  toggle('gravity_payments', 'Gravity Payments', 'payments'),
  toggle('firstmile_payments', '1st Mile Payments', 'payments'),
  toggle('basys', 'Basys Payments', 'payments'),
  toggle('easypay', 'EasyPay', 'payments', {
    help: 'Controls shared EasyPay settings and notification options.',
  }),
  toggle('advanced_camera_upload', 'Advanced camera upload', 'media'),
  toggle('capture_geo_location', 'Capture photo location', 'media', { sensitive: true }),
  toggle('ai_fraud_detection', 'AI image verification', 'media'),
  toggle('chat_gpt', 'ChatGPT assistance', 'ai'),
  {
    key: 'chat_gpt_plan',
    label: 'AI plan',
    group: 'ai',
    type: 'select',
    optionsKey: 'chatGptPlans',
    valueType: 'positive-integer',
    placeholder: 'No plan selected',
    help: 'Select from the plans available to this shop.',
  },
];

export function isFeatureControlValue(field, value) {
  if (field.type === 'boolean') return typeof value === 'boolean';
  if (value === null) return true;
  if (field.valueType === 'positive-integer') return Number.isSafeInteger(value) && value > 0;
  if (field.allowedValues) return field.allowedValues.includes(value);
  return typeof value === 'string' && value.length <= (field.max || Infinity);
}

export function featureControlFields(features = {}, options = {}) {
  return FEATURE_CONTROLS.filter((field) => Object.hasOwn(features, field.key)).map((field) => {
    const choices =
      field.optionsKey && Array.isArray(options[field.optionsKey])
        ? options[field.optionsKey].filter(
            (option) =>
              typeof option?.label === 'string' &&
              isFeatureControlValue(field, option.value) &&
              option.value !== null &&
              option.disabled !== true,
          )
        : [];
    const unavailable = Boolean(field.optionsKey && !choices.length);
    const current = features[field.key];
    const displayedChoices =
      field.optionsKey && current != null && !choices.some((option) => option.value === current)
        ? [
            ...choices,
            {
              label:
                field.key === 'chat_gpt_plan'
                  ? `Current plan (#${current})`
                  : `Current provider (${current})`,
              value: current,
              disabled: true,
            },
          ]
        : choices;
    return {
      ...field,
      ...(field.optionsKey ? { options: displayedChoices, disabled: unavailable } : {}),
      ...(unavailable ? { help: 'Choices are unavailable for this shop.' } : {}),
      validate(value) {
        if (!isFeatureControlValue(field, value == null || value === '' ? null : value))
          return field.type === 'boolean' ? 'Choose on or off.' : 'Choose a supported value.';
        // A retired option can remain selected while another setting changes.
        // It is displayed read-only in the list and omitted from changed-field saves.
        if (field.optionsKey && value === current) return '';
        if (
          field.optionsKey &&
          value != null &&
          value !== '' &&
          !choices.some((option) => option.value === value)
        )
          return 'Choose an available option.';
        return '';
      },
    };
  });
}
